import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Target,
  Building,
  Calendar,
  BarChart3,
  FileText,
  Plus,
  Edit,
  Flag
} from 'lucide-react';
import { AllocationManager } from '@/components/allocation';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassTabs,
  ProgressBar,
} from '@/design-system';
import {
  fetchEpicAllocations,
  createEpicAllocation,
  updateEpicAllocation,
  deleteEpicAllocation,
  clearAllocationError,
  fetchEpicById
} from '@/store/slices/epicsSlice';
import {
  createMilestone,
  updateMilestone,
  deleteMilestone
} from '@/store/slices/milestonesSlice';
import type { Epic, EpicAllocation, Milestone } from '@/types';
import type { RootState, AppDispatch } from '@/store';
import MilestoneList from './MilestoneList';
import { formatCurrencyCompact } from '@/utils/formatters';

interface EpicDetailModalProps {
  epic: Epic;
  onClose: () => void;
  initialTab?: string;
}

const EpicDetailModal: React.FC<EpicDetailModalProps> = ({
  epic,
  onClose,
  initialTab = 'overview'
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { allocations, isManagingAllocations, allocationError } = useSelector(
    (state: RootState) => state.epics
  );
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [localMilestones, setLocalMilestones] = useState<Milestone[]>(epic.milestones || []);

  // Update local milestones when epic changes
  useEffect(() => {
    setLocalMilestones(epic.milestones || []);
  }, [epic.milestones]);

  // Fetch allocations when component mounts or epic changes
  useEffect(() => {
    if (epic.id) {
      dispatch(fetchEpicAllocations(epic.id));
    }

    // Clear allocation error when component unmounts
    return () => {
      dispatch(clearAllocationError());
    };
  }, [dispatch, epic.id]);

  const getStatusBadgeProps = (status: string): { variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info'; status?: 'committed' | 'stretch' | 'draft' | 'prioritized' } => {
    switch (status) {
      case 'COMMITTED':
        return { status: 'committed' };
      case 'STRETCH':
        return { status: 'stretch' };
      case 'DELIVERED':
        return { variant: 'success' };
      case 'CANCELLED':
        return { variant: 'danger' };
      case 'PRIORITIZED':
        return { status: 'prioritized' };
      case 'DRAFT':
        return { status: 'draft' };
      default:
        return { variant: 'default' };
    }
  };

  const calculateProgress = () => {
    if (epic.requestedTeamWeeks === 0) return 0;
    return Math.round((epic.allocatedTeamWeeks / epic.requestedTeamWeeks) * 100);
  };

  const estimatedCost = (epic.requestedTeamWeeks ?? 0) * 89 * 40; // Using blended rate
  const allocatedCost = epic.allocatedCost ?? (epic.allocatedTeamWeeks * 89 * 40);

  // Allocation management handlers
  const handleCreateAllocation = async (allocation: Omit<EpicAllocation, 'id' | 'createdAt' | 'updatedAt'>) => {
    await dispatch(createEpicAllocation({ epicId: epic.id, allocation })).unwrap();
  };

  const handleUpdateAllocation = async (allocationId: string, data: Partial<EpicAllocation>) => {
    await dispatch(updateEpicAllocation({ allocationId, data })).unwrap();
  };

  const handleDeleteAllocation = async (allocationId: string) => {
    try {
      await dispatch(deleteEpicAllocation(allocationId)).unwrap();
    } catch {
      // Refetch allocations to resync UI with server state
      dispatch(fetchEpicAllocations(epic.id));
      throw new Error('Allocation not found. The list has been refreshed.');
    }
  };

  // Milestone management handlers
  const handleCreateMilestone = async (data: Partial<Milestone>) => {
    try {
      const result = await dispatch(createMilestone({
        epicId: epic.id,
        targetDate: data.targetDate!,
        completionPercentage: data.completionPercentage ?? 0,
        description: data.description,
        type: data.type!,
      })).unwrap();

      // Update local milestones state (optimistic update)
      if (result.milestone) {
        setLocalMilestones(prev => [...prev, result.milestone]);
      }
    } catch (error) {
      console.error('Failed to create milestone:', error);
      throw error;
    }
  };

  const handleUpdateMilestone = async (id: string, data: Partial<Milestone>) => {
    try {
      const result = await dispatch(updateMilestone({ id, data })).unwrap();

      // Update local milestones state (optimistic update)
      if (result.milestone) {
        setLocalMilestones(prev => prev.map(m =>
          m.id === id ? result.milestone : m
        ));
      }
    } catch (error) {
      console.error('Failed to update milestone:', error);
      throw error;
    }
  };

  const handleDeleteMilestone = async (id: string) => {
    try {
      await dispatch(deleteMilestone(id)).unwrap();

      // Update local milestones state (optimistic update)
      setLocalMilestones(prev => prev.filter(m => m.id !== id));
    } catch (error) {
      console.error('Failed to delete milestone:', error);
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-gradient-to-br from-accent-blue/20 to-accent-violet/20">
              <Target className="w-5 h-5 text-accent-blue" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text)]">{epic.name}</h2>
            <GlassBadge {...getStatusBadgeProps(epic.status)}>
              {epic.status}
            </GlassBadge>
            {epic.priority && (
              <GlassBadge variant="default">
                Priority #{epic.priority}
              </GlassBadge>
            )}
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <GlassCard variant="subtle" padding="sm" className="text-center">
          <div className="text-2xl font-bold text-accent-blue">
            {epic.requestedTeamWeeks}
          </div>
          <div className="text-xs text-[var(--muted)]">Requested TWs</div>
        </GlassCard>

        <GlassCard variant="subtle" padding="sm" className="text-center">
          <div className="text-2xl font-bold text-accent-violet">
            {epic.allocatedTeamWeeks}
          </div>
          <div className="text-xs text-[var(--muted)]">Allocated TWs</div>
        </GlassCard>

        <GlassCard variant="subtle" padding="sm" className="text-center">
          <div className="text-2xl font-bold text-status-success">
            {calculateProgress()}%
          </div>
          <div className="text-xs text-[var(--muted)]">Progress</div>
        </GlassCard>

        <GlassCard variant="subtle" padding="sm" className="text-center">
          <div className="text-2xl font-bold text-accent-orange">
            {epic._count?.features || 0}
          </div>
          <div className="text-xs text-[var(--muted)]">Features</div>
        </GlassCard>

        <GlassCard variant="subtle" padding="sm" className="text-center">
          <div className="text-2xl font-bold text-status-success">
            {formatCurrencyCompact(allocatedCost, { fallback: '$0' })}
          </div>
          <div className="text-xs text-[var(--muted)]">Allocated Cost</div>
        </GlassCard>
      </div>

      {/* Tabs */}
      <GlassTabs
        tabs={[
          { id: 'overview', label: 'Overview', icon: <FileText className="w-4 h-4" /> },
          { id: 'features', label: 'Features', icon: <Target className="w-4 h-4" /> },
          { id: 'milestones', label: 'Milestones', icon: <Flag className="w-4 h-4" /> },
          { id: 'allocations', label: 'Allocations', icon: <BarChart3 className="w-4 h-4" /> },
          { id: 'timeline', label: 'Timeline', icon: <Calendar className="w-4 h-4" /> }
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="w-full justify-center"
      />

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Basic Information */}
            <GlassCard variant="subtle" padding="md">
              <div className="flex items-center gap-2 mb-4">
                <Building className="w-4 h-4 text-accent-blue" />
                <h3 className="text-sm font-semibold text-[var(--text)]">Basic Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Investible Structure</label>
                  <div className="mt-1 space-y-1">
                    <p className="text-sm text-[var(--text)]">
                      {epic.investibleStructure?.name || 'No Structure'}
                    </p>
                    {epic.investibleStructure?.owner && (
                      <p className="text-xs text-[var(--muted)]">
                        Owner: {epic.investibleStructure.owner.firstName} {epic.investibleStructure.owner.lastName}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Program</label>
                  <p className="text-sm text-[var(--text)] mt-1 flex items-center gap-2">
                    {epic.program?.name || 'Unassigned'}
                    {epic.isPrimaryProgram && epic.program && (
                      <GlassBadge variant="primary" size="sm">Primary</GlassBadge>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Created</label>
                  <p className="text-sm text-[var(--text)] mt-1">
                    {new Date(epic.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Last Updated</label>
                  <p className="text-sm text-[var(--text)] mt-1">
                    {new Date(epic.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </GlassCard>

            {/* Description */}
            {epic.description && (
              <GlassCard variant="subtle" padding="md">
                <h3 className="text-sm font-semibold text-[var(--text)] mb-3">Description</h3>
                <p className="text-sm text-[var(--muted)] leading-relaxed whitespace-pre-wrap">
                  {epic.description}
                </p>
              </GlassCard>
            )}

            {/* Allocation Summary */}
            <GlassCard variant="subtle" padding="md">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-4 h-4 text-accent-violet" />
                <h3 className="text-sm font-semibold text-[var(--text)]">Allocation Summary</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Requested</label>
                  <p className="text-xl font-bold text-accent-blue mt-1">
                    {epic.requestedTeamWeeks} TWs
                  </p>
                  <p className="text-xs text-[var(--muted)]">
                    ${estimatedCost.toLocaleString()} estimated
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Allocated</label>
                  <p className="text-xl font-bold text-accent-violet mt-1">
                    {epic.allocatedTeamWeeks} TWs
                  </p>
                  <p className="text-xs text-[var(--muted)]">
                    ${allocatedCost.toLocaleString()} committed
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Remaining</label>
                  <p className={`text-xl font-bold mt-1 ${
                    (epic.requestedTeamWeeks - epic.allocatedTeamWeeks) >= 0
                      ? 'text-status-success'
                      : 'text-status-error'
                  }`}>
                    {epic.requestedTeamWeeks - epic.allocatedTeamWeeks} TWs
                  </p>
                  <p className="text-xs text-[var(--muted)]">
                    {calculateProgress()}% complete
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="space-y-1 mb-3">
                <div className="flex justify-between text-xs text-[var(--muted)]">
                  <span>Allocation Progress</span>
                  <span>{calculateProgress()}%</span>
                </div>
                <ProgressBar
                  value={Math.min(calculateProgress(), 100)}
                  color={calculateProgress() > 100 ? 'rose' : calculateProgress() >= 80 ? 'emerald' : 'blue'}
                  size="md"
                />
              </div>
              {calculateProgress() > 100 && (
                <p className="text-xs text-status-error">
                  Over-allocated by {calculateProgress() - 100}%
                </p>
              )}

              {/* Quick Actions */}
              <div className="mt-4 pt-4 border-t border-[var(--border)]">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-[var(--muted)]">
                    {allocations.length} allocation{allocations.length !== 1 ? 's' : ''} across programs and teams
                  </p>
                  <GlassButton
                    size="sm"
                    variant="secondary"
                    onClick={() => setActiveTab('allocations')}
                  >
                    Manage Allocations
                  </GlassButton>
                </div>
              </div>
            </GlassCard>
          </div>
        )}

        {activeTab === 'features' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[var(--text)]">Features</h3>
              <GlassButton size="sm" leftIcon={<Plus className="w-4 h-4" />}>
                Add Feature
              </GlassButton>
            </div>

            {epic.features && epic.features.length > 0 ? (
              <div className="space-y-3">
                {epic.features.map((feature) => (
                  <GlassCard key={feature.id} variant="subtle" padding="sm" hover>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-[var(--text)]">{feature.name}</h4>
                        {feature.description && (
                          <p className="text-xs text-[var(--muted)] mt-1 line-clamp-2">
                            {feature.description}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs text-[var(--muted)]">
                            Q{feature.targetQuarter}
                          </span>
                          <span className="text-xs text-[var(--muted)]">
                            {feature.estimatedTeamWeeks} TWs
                          </span>
                          <GlassBadge
                            size="sm"
                            variant={
                              feature.status === 'SHOVEL_READY' ? 'success' :
                              feature.status === 'IN_REFINEMENT' ? 'warning' : 'default'
                            }
                          >
                            {feature.status.replace('_', ' ')}
                          </GlassBadge>
                        </div>
                      </div>
                      <GlassButton variant="ghost" size="sm">
                        <Edit className="w-4 h-4" />
                      </GlassButton>
                    </div>
                  </GlassCard>
                ))}
              </div>
            ) : (
              <GlassCard variant="subtle" padding="lg" className="text-center">
                <Target className="w-10 h-10 text-[var(--muted)] mx-auto mb-3 opacity-50" />
                <h3 className="text-sm font-medium text-[var(--muted)] mb-1">No Features</h3>
                <p className="text-xs text-[var(--muted)] mb-4">Add features to break down this EPIC</p>
                <GlassButton size="sm" leftIcon={<Plus className="w-4 h-4" />}>
                  Add First Feature
                </GlassButton>
              </GlassCard>
            )}
          </div>
        )}

        {activeTab === 'allocations' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[var(--text)]">Capacity Allocations</h3>
            </div>

            {allocationError && (
              <GlassCard variant="subtle" padding="sm" className="border border-status-error/30 bg-status-error/5">
                <p className="text-status-error text-sm">{allocationError}</p>
              </GlassCard>
            )}

            <AllocationManager
              epic={epic}
              allocations={allocations}
              onCreateAllocation={handleCreateAllocation}
              onUpdateAllocation={handleUpdateAllocation}
              onDeleteAllocation={handleDeleteAllocation}
              isLoading={isManagingAllocations}
            />
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="space-y-4">
            {/* Epic Timeline Info */}
            <GlassCard variant="subtle" padding="md">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-4 h-4 text-accent-blue" />
                <h3 className="text-sm font-semibold text-[var(--text)]">EPIC Timeline</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Created</label>
                  <p className="text-sm text-[var(--text)] mt-1">
                    {new Date(epic.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Last Updated</label>
                  <p className="text-sm text-[var(--text)] mt-1">
                    {new Date(epic.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Total Features</label>
                  <p className="text-sm text-[var(--text)] mt-1">
                    {epic.features?.length || 0} features
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Estimated Effort</label>
                  <p className="text-sm text-[var(--text)] mt-1">
                    {epic.features?.reduce((sum, f) => sum + (f.estimatedTeamWeeks || 0), 0) || 0} Team Weeks
                  </p>
                </div>
              </div>
            </GlassCard>

            {/* Features by Quarter */}
            {epic.features && epic.features.length > 0 ? (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-[var(--text)]">Feature Milestones by Quarter</h4>

                {[1, 2, 3, 4].map(quarter => {
                  const quarterFeatures = epic.features.filter(f => f.targetQuarter === quarter);
                  if (quarterFeatures.length === 0) return null;

                  return (
                    <GlassCard key={quarter} variant="subtle" padding="md">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-[var(--text)]">Q{quarter}</span>
                        <GlassBadge variant="default" size="sm">
                          {quarterFeatures.length} feature{quarterFeatures.length !== 1 ? 's' : ''}
                        </GlassBadge>
                      </div>
                      <div className="space-y-2">
                        {quarterFeatures.map(feature => (
                          <div
                            key={feature.id}
                            className="flex items-center justify-between p-2.5 rounded-lg bg-[var(--surface)]/50 border border-white/5"
                          >
                            <div className="flex-1 min-w-0">
                              <h5 className="text-sm font-medium text-[var(--text)] truncate">{feature.name}</h5>
                              <div className="flex items-center gap-2 mt-1">
                                {feature.leadProgram && (
                                  <span className="text-xs text-[var(--muted)]">
                                    {feature.leadProgram.name}
                                  </span>
                                )}
                                <span className="text-xs text-[var(--muted)]">
                                  {feature.estimatedTeamWeeks || 0} TWs
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-3">
                              <div className="text-right">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-12 bg-[var(--surface-muted)] rounded-full h-1.5">
                                    <div
                                      className={`h-1.5 rounded-full ${
                                        feature.readinessScore >= 80 ? 'bg-status-success' :
                                        feature.readinessScore >= 40 ? 'bg-status-warning' : 'bg-status-error'
                                      }`}
                                      style={{ width: `${feature.readinessScore}%` }}
                                    />
                                  </div>
                                  <span className={`text-xs font-medium ${
                                    feature.readinessScore >= 80 ? 'text-status-success' :
                                    feature.readinessScore >= 40 ? 'text-status-warning' : 'text-status-error'
                                  }`}>
                                    {feature.readinessScore}%
                                  </span>
                                </div>
                              </div>
                              <GlassBadge
                                size="sm"
                                variant={
                                  feature.status === 'SHOVEL_READY' ? 'success' :
                                  feature.status === 'IN_REFINEMENT' ? 'warning' : 'default'
                                }
                              >
                                {feature.status.replace('_', ' ')}
                              </GlassBadge>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Quarter Summary */}
                      <div className="mt-3 pt-3 border-t border-[var(--border)]">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-[var(--muted)]">Quarter Total</span>
                          <div className="flex items-center gap-3">
                            <span className="text-[var(--muted)]">
                              {quarterFeatures.reduce((sum, f) => sum + (f.estimatedTeamWeeks || 0), 0)} TWs
                            </span>
                            <span className="text-[var(--muted)]">
                              Avg: {Math.round(
                                quarterFeatures.reduce((sum, f) => sum + f.readinessScore, 0) / quarterFeatures.length
                              )}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </GlassCard>
                  );
                })}
              </div>
            ) : (
              <GlassCard variant="subtle" padding="lg" className="text-center">
                <Target className="w-10 h-10 text-[var(--muted)] mx-auto mb-3 opacity-50" />
                <h3 className="text-sm font-medium text-[var(--muted)] mb-1">No Features Yet</h3>
                <p className="text-xs text-[var(--muted)]">Add features to this EPIC to see the timeline</p>
              </GlassCard>
            )}
          </div>
        )}

        {activeTab === 'milestones' && (
          <MilestoneList
            milestones={localMilestones}
            epicId={epic.id}
            isLoading={false}
            onCreateMilestone={handleCreateMilestone}
            onUpdateMilestone={handleUpdateMilestone}
            onDeleteMilestone={handleDeleteMilestone}
          />
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--border)]">
        <GlassButton variant="ghost" onClick={onClose}>
          Close
        </GlassButton>
        <GlassButton leftIcon={<Edit className="w-4 h-4" />}>
          Edit EPIC
        </GlassButton>
      </div>
    </div>
  );
};

export default EpicDetailModal;
